import { onMounted, onUnmounted, ref } from 'vue';
import emitter from '@/mitt';
import { GlobalEventAction, SESSION_EXPIRED_REDIRECT_DELAY_MS } from '../constant/enums';
import { resolveParentAppOrigin } from './useParentAppToken';

/**
 * Host-app (DreeSuite) session expiry.
 *
 * The host-app auth path only proves that an APM token is *present* — never that it
 * is still valid. A 401 from any backend call is therefore the authoritative signal
 * that the session is gone. When that happens the app shows a blocking notice and,
 * five seconds later, hands the browser back to the host application so it can run
 * its own login flow. The notice's button performs the same redirect immediately.
 *
 * Module-scoped latch: a page load fans out several requests in parallel, so a dead
 * token produces a burst of 401s. Only the first one may start a timer.
 */
let hasSessionExpired = false;

const redirectToParentApp = () => {
  const loginUrl = resolveParentAppOrigin();
  try {
    // Assign only — reading `window.top.location` throws when the host is a
    // different origin. Navigates the whole tab rather than just this iframe.
    (window.top as Window).location.href = loginUrl;
  } catch (navigationError) {
    // Thrown when the host frames us with a sandbox that forbids top navigation.
    console.error(
      'Platmap: top-level navigation to the host app was blocked, redirecting this frame instead',
      { loginUrl, reason: (navigationError as Error)?.message }
    );
    window.location.href = loginUrl;
  }
};

export function useSessionExpiry() {
  const isSessionExpired = ref(false);
  let redirectTimer: number | undefined;
  let hasRedirected = false;

  /**
   * Backs both the countdown and the notice's button. Cancelling the timer before
   * navigating means a click cannot race the timeout into a second redirect.
   */
  const redirectNow = () => {
    if (hasRedirected) return;
    hasRedirected = true;
    window.clearTimeout(redirectTimer);
    redirectToParentApp();
  };

  const handleUnauthorized = () => {
    if (hasSessionExpired) return;
    hasSessionExpired = true;
    isSessionExpired.value = true;
    redirectTimer = window.setTimeout(redirectNow, SESSION_EXPIRED_REDIRECT_DELAY_MS);
  };

  onMounted(() => {
    emitter.on(GlobalEventAction.Unauthorized, handleUnauthorized);
  });

  onUnmounted(() => {
    emitter.off(GlobalEventAction.Unauthorized, handleUnauthorized);
    // Without this, navigating away inside the five-second window would still yank
    // the browser off whatever page the user moved to.
    window.clearTimeout(redirectTimer);
  });

  return { isSessionExpired, redirectNow };
}
